// Libs
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dropdown, DropdownMenu, DropdownToggle, ListGroup, ListGroupItem, Spinner } from "reactstrap";
import { debounce, map, noop } from "lodash";
import classnames from "classnames";

// Components, Views, Screens


// Hooks, Utils, Helpers
import { useService } from "../../../hooks/useService";
import { useLoading } from "../../../hooks/useLoading";


// Styles, assets
import classes from "./ContentCategoryDropdown.module.scss";
import TableSearch from "../../Table/tableSearch";
import ContentCategoriesService from "../../../../services/ContentCategoriesService";

const DEFAULT_LIMIT = 100;
const MIN_SEARCH_LENGTH = 3;


export const ContentCategoryDropdown = ({ onChange, value, placeholder, label, error, handleBlur = noop }) => {
    /**
     * @type {ContentCategoriesService}
     */
    const contentCategoriesService = useService(ContentCategoriesService);

    const [isOpen, updateIsOpen] = useState(false);
    const [categories, setCategories] = useState([]);
    const [page, setPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [isLoading, { registerPromise }] = useLoading(true);
    const lastElementRef = useRef(null);
    const [search, setSearch] = useState("");


    const showPlaceholder = !value?.id;

    const searchRequest = useMemo(() => {
            return search && search.toString()?.trim()?.length >= MIN_SEARCH_LENGTH ? search : undefined;
        },
        [search]);

    const mapCategoryTypesToDropdown = (data = []) => {
        return data.map((item) => ({ id: item.id, name: item.fullName, tags: map(item.categoriesTags, "tag.name") }));
    };

    const setNextPage = () => {
        if (isLoading || !hasNextPage) return;
        setPage(page + 1);
    };

    const debounceSetNextPage = debounce(setNextPage, 300);

    const loadCategories = () => {
        const params = { limit: DEFAULT_LIMIT, offset: 0, query: searchRequest };

        registerPromise(contentCategoriesService.getContentCategories(params))
            .then((result => {
                setCategories(mapCategoryTypesToDropdown(result.data));
                setHasNextPage(result?.pagination?.nextOffset < result?.pagination?.totalCount);
                setPage(1);
            }));
    };

    const loadMoreCategories = () => {
        if (isLoading || !hasNextPage) return;

        const nextOffset = (page) * DEFAULT_LIMIT;

        const params = { limit: DEFAULT_LIMIT, offset: nextOffset, query: searchRequest };

        registerPromise(contentCategoriesService.getContentCategories(params))
            .then((result => {
                setCategories([...categories, ...mapCategoryTypesToDropdown(result.data)]);
                setHasNextPage(result?.pagination?.nextOffset < result?.pagination?.totalCount);
                setPage(page + 1);
            }));
    };

    useEffect(() => {
        if (!lastElementRef.current) return;

        const observer = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting) {
                loadMoreCategories();
            }
        });

        observer.observe(lastElementRef.current);

        return () => {
            observer.disconnect();
        };
    }, [loadMoreCategories, lastElementRef.current]);

    useEffect(() => {
        loadCategories();
    }, [searchRequest]);

    return (<>
            {!!label && <label>{label}</label>}
            <section className="d-flex align-items-center w-100">
                <Dropdown
                    isOpen={isOpen}
                    toggle={() => {
                        if (isOpen) {
                            handleBlur();
                        }
                        updateIsOpen(prevState => !prevState);
                    }}
                    className="d-inline-block filter-dropdown cursor-pointer result-filter w-100"
                    direction="down"
                >
                    <DropdownToggle
                        className={classnames('filter-toggle max-w-100 pe-0',
                            {
                                'with-border': isOpen,
                                'is-invalid': !!error
                            }
                        )}
                        tag="section"
                    >
                <span
                    className={classnames('ms-2 me-1 pointer-events-none user-select-none text-truncate', { 'text-secondary': showPlaceholder })}>
            {
                showPlaceholder
                    ? placeholder || 'Select category'
                    : value?.name
            }
                </span>
                        <i className={classnames('mdi mdi-chevron-down pointer-events-none user-select-none me-2', { 'mdi-rotate-180': isOpen })}/>
                    </DropdownToggle>

                    <DropdownMenu className="filter-menu pb-1 px-1 w-100 top-50" flip={false}>
                        <TableSearch onSearch={setSearch} search={search} className="my-2"/>

                        <section>
                            <ListGroup>
                                <div className={classnames(classes.ItemsWrapper, "custom-scrollbar")}>
                                    {categories.map((item, index) => (
                                        <ListGroupItem className="bg-transparent border-0 p-0" key={item.id}>
                                            <div
                                                className={classnames(classes.Item, value?.id === item.id && classes.Active)}
                                                onClick={() => {
                                                    onChange(item);
                                                    updateIsOpen(false);
                                                }}
                                            >
                                                {item.name}
                                            </div>
                                            {
                                                index === categories.length - 1
                                                    ? <div
                                                        ref={lastElementRef}
                                                        className="d-flex justify-content-center">
                                                    </div>
                                                    : null
                                            }
                                        </ListGroupItem>
                                    ))}
                                    {isLoading && <div className="d-flex my-2 justify-content-center">
                                        <Spinner size="sm" color="primary"/>
                                    </div>}
                                </div>
                            </ListGroup>
                        </section>
                    </DropdownMenu>
                </Dropdown>
            </section>
        </>
    )
        ;
};

